import {
  Roles,
  isPartnerRole,
  type PartnerFeaturesMap,
  type PartnerSubscriptionTier,
} from '@/constants/roles';
import { getVerifiedSession } from '@/lib/auth/verifiedSession';
import { safeGetLocalStorage, safeSetLocalStorage } from '@/lib/utils/storageUtils';
import {
  EMPTY_PORTAL_CAPABILITIES,
  parsePortalCapabilities,
  type PortalCapabilities,
} from './capabilities';
import { readPortalSessionPayload } from './internal/supabase';

const IMPERSONATION_STORAGE_KEY = 'portal_impersonation_target';

export type PortalViewAsTarget = 'admin' | `partner:${string}`;

export type PortalActor = {
  userId: string;
  role: Roles.Admin | Roles.PartnerStandard | Roles.PartnerPremium;
  partnerId: string | null;
};

export type PortalEffectiveSubject = {
  mode: 'admin' | 'partner';
  partnerId: string | null;
  role: Roles.Admin | Roles.PartnerStandard | Roles.PartnerPremium;
  subscriptionTier: PartnerSubscriptionTier | null;
  features: PartnerFeaturesMap;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
};

export type ImpersonationPartnerOption = {
  partner_id: string;
  name: string;
  subscription_tier: PartnerSubscriptionTier;
};

export type PortalSessionSnapshot = {
  state: 'ready' | 'unauthorized';
  actor: PortalActor | null;
  viewAsTarget: PortalViewAsTarget;
  viewAsOptions: ImpersonationPartnerOption[];
  effective: PortalEffectiveSubject | null;
  capabilities: PortalCapabilities;
};

export type PortalSessionFetchResult =
  | { ok: true; snapshot: PortalSessionSnapshot }
  | { ok: false; reason: 'unauthorized' | 'invalid_view_as_target' };

type PortalSessionRpcPayload = {
  success?: boolean;
  error?: string;
  actor?: {
    user_id?: string;
    role?: string;
    partner_id?: string | null;
  };
  view_as_target?: string | null;
  effective?: {
    mode?: string;
    partner_id?: string | null;
    role?: string;
    subscription_tier?: string | null;
    features?: PartnerFeaturesMap;
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
  };
  capabilities?: unknown;
  view_as_options?: ImpersonationPartnerOption[];
};

export class PortalSessionOperationError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'PortalSessionOperationError';
  }
}

function mapSessionRole(
  role: string | null | undefined
): Roles.Admin | Roles.PartnerStandard | Roles.PartnerPremium | null {
  if (role === Roles.Admin) {
    return Roles.Admin;
  }
  if (role === Roles.PartnerPremium || role === Roles.Partner) {
    return Roles.PartnerPremium;
  }
  if (role === Roles.PartnerStandard) {
    return Roles.PartnerStandard;
  }
  return null;
}

function mapSubscriptionTier(value: string | null | undefined): PartnerSubscriptionTier | null {
  if (
    value === 'individual' ||
    value === 'business' ||
    value === 'enterprise' ||
    value === 'api' ||
    value === 'affiliate'
  ) {
    return value;
  }
  return null;
}

export function normalizePortalViewAsTarget(target: unknown): PortalViewAsTarget {
  if (target === 'admin') {
    return 'admin';
  }

  if (typeof target !== 'string' || !target.startsWith('partner:')) {
    return 'admin';
  }

  const partnerId = target.slice('partner:'.length).trim();
  return partnerId ? `partner:${partnerId}` : 'admin';
}

export function readStoredViewAsTarget(): PortalViewAsTarget {
  return normalizePortalViewAsTarget(safeGetLocalStorage(IMPERSONATION_STORAGE_KEY));
}

export function writeStoredViewAsTarget(target: PortalViewAsTarget): void {
  safeSetLocalStorage(IMPERSONATION_STORAGE_KEY, normalizePortalViewAsTarget(target));
}

function viewAsTargetToRpcArg(target: PortalViewAsTarget): string | null {
  const normalizedTarget = normalizePortalViewAsTarget(target);
  if (normalizedTarget === 'admin') {
    return null;
  }
  return normalizedTarget.slice('partner:'.length);
}

export function readStoredViewAsPartnerId(): string | null {
  const target = readStoredViewAsTarget();
  return viewAsTargetToRpcArg(target);
}

function viewAsTargetFromRpcResponse(target: string | null | undefined): PortalViewAsTarget {
  if (target === 'admin') {
    return 'admin';
  }

  if (typeof target !== 'string') {
    return 'admin';
  }

  const trimmed = target.trim();
  if (!trimmed || trimmed === 'admin') {
    return 'admin';
  }

  return normalizePortalViewAsTarget(
    trimmed.startsWith('partner:') ? trimmed : `partner:${trimmed}`
  );
}

type DecodedPortalSession =
  | { kind: 'ready'; snapshot: PortalSessionSnapshot }
  | { kind: 'unauthorized' }
  | { kind: 'invalid_view_as_target' };

function decodePortalSession(data: unknown): DecodedPortalSession {
  const payload = data as PortalSessionRpcPayload | null;

  if (!payload || payload.success !== true) {
    if (payload?.error === 'INVALID_VIEW_AS_TARGET') {
      return { kind: 'invalid_view_as_target' };
    }
    if (
      payload?.error === 'UNAUTHORIZED' ||
      payload?.error === 'NOT_PARTNER' ||
      payload?.error === 'SUBSCRIPTION_CANCELED'
    ) {
      return { kind: 'unauthorized' };
    }
    throw new Error('Portal Account Scope returned an unexpected outcome.');
  }

  const actorRole = mapSessionRole(payload.actor?.role);
  if (!payload.actor?.user_id || !actorRole) {
    throw new Error('Portal Account Scope returned an invalid actor.');
  }

  const effectiveRole = mapSessionRole(payload.effective?.role);
  const effectiveMode = payload.effective?.mode;
  if (!effectiveRole || (effectiveMode !== 'admin' && effectiveMode !== 'partner')) {
    throw new Error('Portal Account Scope returned an invalid effective subject.');
  }

  const viewAsTarget = viewAsTargetFromRpcResponse(payload.view_as_target ?? 'admin');
  const capabilities = parsePortalCapabilities(payload.capabilities);
  const subscriptionTier = mapSubscriptionTier(payload.effective?.subscription_tier);

  return {
    kind: 'ready',
    snapshot: {
      state: 'ready',
      actor: {
        userId: payload.actor.user_id,
        role: actorRole,
        partnerId: payload.actor.partner_id ?? null,
      },
      viewAsTarget,
      viewAsOptions: Array.isArray(payload.view_as_options) ? payload.view_as_options : [],
      effective: {
        mode: effectiveMode,
        partnerId: payload.effective?.partner_id ?? null,
        role: effectiveRole,
        subscriptionTier,
        features: payload.effective?.features ?? {},
        stripeCustomerId: payload.effective?.stripe_customer_id ?? null,
        stripeSubscriptionId: payload.effective?.stripe_subscription_id ?? null,
      },
      capabilities,
    },
  };
}

export async function fetchPortalSession(
  viewAsTarget: PortalViewAsTarget = 'admin'
): Promise<PortalSessionFetchResult> {
  const target = normalizePortalViewAsTarget(viewAsTarget);
  try {
    const decoded = decodePortalSession(
      await readPortalSessionPayload(viewAsTargetToRpcArg(target))
    );
    if (decoded.kind === 'invalid_view_as_target') {
      return { ok: false, reason: 'invalid_view_as_target' };
    }
    if (decoded.kind === 'unauthorized') {
      return { ok: false, reason: 'unauthorized' };
    }
    return { ok: true, snapshot: decoded.snapshot };
  } catch (error) {
    if (error instanceof PortalSessionOperationError) {
      throw error;
    }
    throw new PortalSessionOperationError('Could not load Portal Account Scope.', { cause: error });
  }
}

/** React Router clientLoader: resolve portal session before hydration. */
export async function loadPortalSession(): Promise<PortalSessionSnapshot> {
  try {
    const session = await getVerifiedSession();

    if (!session?.user) {
      return createUnauthorizedSnapshot();
    }

    return loadPortalSessionWithStoredTarget();
  } catch (err) {
    console.error('Loader error fetching Portal session:', err);
    return createUnauthorizedSnapshot();
  }
}

export type PartnerPortalLoaderData = PortalSessionSnapshot;

export async function loadPortalSessionWithStoredTarget(): Promise<PortalSessionSnapshot> {
  const storedTarget = readStoredViewAsTarget();
  const initialResult = await fetchPortalSession(storedTarget);

  if (initialResult.ok) {
    writeStoredViewAsTarget(initialResult.snapshot.viewAsTarget);
    return initialResult.snapshot;
  }

  if (initialResult.reason === 'invalid_view_as_target') {
    writeStoredViewAsTarget('admin');
    const resetResult = await fetchPortalSession('admin');
    if (resetResult.ok) {
      return resetResult.snapshot;
    }
    return createUnauthorizedSnapshot();
  }

  return createUnauthorizedSnapshot();
}

export function createUnauthorizedSnapshot(): PortalSessionSnapshot {
  return {
    state: 'unauthorized',
    actor: null,
    viewAsTarget: 'admin',
    viewAsOptions: [],
    effective: null,
    capabilities: EMPTY_PORTAL_CAPABILITIES,
  };
}

export function isAdminActor(actor: PortalActor | null): boolean {
  return actor?.role === Roles.Admin;
}

export function isPartnerActor(actor: PortalActor | null): boolean {
  return actor != null && isPartnerRole(actor.role);
}
